<script setup lang="ts">
/**
 * Rangée d'atouts des pages charte « apple » (Karim 2026-10-18) : grand
 * titre (slot `titre` pour un mot mis en valeur), 2 à 4 tuiles #f5f5f7 —
 * icône sobre qui passe au bleu Donoor au survol, titre (fin facultative en
 * gris), texte.
 */
import { TITRE_CARTE_APPLE, TITRE_SECTION_APPLE } from "~/utils/titresApple";

export interface Atout {
  titre: string;
  /** Fin du titre, en gris (facultatif). */
  accent?: string;
  texte: string;
  icone: string;
}

const props = withDefaults(
  defineProps<{
    titre?: string;
    description?: string;
    atouts: Atout[];
    /** Fond de la section. */
    fond?: "blanc" | "gris";
  }>(),
  { titre: undefined, description: undefined, fond: "blanc" },
);
</script>

<template>
  <section class="px-4 py-20 sm:px-8 lg:py-28" :class="props.fond === 'gris' ? 'bg-[#f5f5f7]' : 'bg-white'">
    <div class="mx-auto max-w-[1120px]">
      <div v-if="props.titre || $slots.titre" class="mb-12 max-w-[820px]">
        <h2 class="pa-reveal mb-5 text-[32px] text-[#1d1d1f] [text-wrap:balance] sm:text-[48px]" :style="TITRE_SECTION_APPLE">
          <slot name="titre">{{ props.titre }}</slot>
        </h2>
        <p v-if="props.description" class="pa-reveal max-w-[680px] text-[17px] font-medium leading-[1.55] text-[#424245] sm:text-[19px]">
          {{ props.description }}
        </p>
      </div>
      <div class="grid gap-5" :class="props.atouts.length === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : props.atouts.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'">
        <article
          v-for="(a, i) in props.atouts"
          :key="a.titre"
          class="tuile pa-reveal rounded-[28px] p-8"
          :class="props.fond === 'gris' ? 'bg-white' : 'bg-[#f5f5f7]'"
          :style="{ '--d': `${i * 90}ms` }"
        >
          <span class="tuile-icone mb-6 flex size-12 items-center justify-center rounded-2xl text-[#1d1d1f]" :class="props.fond === 'gris' ? 'bg-[#f5f5f7]' : 'bg-white'">
            <UIcon :name="a.icone" class="size-6" aria-hidden="true" />
          </span>
          <h3 class="mb-3 text-[22px] text-[#1d1d1f]" :style="TITRE_CARTE_APPLE">
            {{ a.titre }}<template v-if="a.accent">{{ " " }}<span class="text-[#86868b]">{{ a.accent }}</span></template>
          </h3>
          <p class="text-[15px] leading-[1.6] text-[#424245]">{{ a.texte }}</p>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped>
.tuile {
  transition:
    transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1),
    box-shadow 0.4s ease;
}
.tuile-icone {
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
}
@media (hover: hover) {
  .tuile:hover {
    transform: translateY(-3px);
    box-shadow: 0 24px 50px -30px rgba(0, 0, 0, 0.3);
  }
  .tuile:hover .tuile-icone {
    background: var(--color-main);
    color: #fff;
  }
}
</style>
